import { Component } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { EndClientReportDataSourceUsage } from '../../models/end-client-reporting-package';

/**
 * The choice behind "Refresh data". Lives in core rather than in `main` because the deck is a
 * full-page route in core and the reports list is in main -- both open this one.
 *
 * The two options are not equivalent, which is the whole reason this is a dialog rather than a
 * second button: pulling the datamap replaces the survey structure every existing deck on the
 * project resolves against, and nothing detects or flags what that changed. The copy has to say so.
 */
@Component({
  selector: 'app-refresh-data-modal',
  standalone: false,
  // The report's popup look -- see .ecr-modal in themes/end-client-reporting.scss.
  host: { class: 'ecr-modal' },
  template: `
    <div class="modal-header">
      <h5 class="modal-title nav-font16 font-weight-bold">Refresh data</h5>
      <button type="button" class="close" aria-label="Close" (click)="close()">
        <span aria-hidden="true">&times;</span>
      </button>
    </div>

    <div class="modal-body">
      <!-- A note rather than a heading: it is a sentence to read before choosing, and set as a bold
           heading it shouted over the choice it introduces. -->
      <p class="refresh-note nav-font14 mb-3">
        <i class="fas fa-triangle-exclamation mr-2" aria-hidden="true"></i>This refreshes the data
        itself, so every report reading it changes — including reports in other projects.
      </p>

      @if (impactLoading) {
        <p class="grey4 nav-font12 mb-3">Checking which reports use this data source…</p>
      } @else if (impactFailed) {
        <p class="grey4 nav-font12 mb-3">
          <i class="fas fa-triangle-exclamation mr-1" aria-hidden="true"></i>Couldn't check which
          reports use this data source. The refresh still affects every one of them.
        </p>
      } @else if (impact) {
        <button type="button" class="btn btn-link p-0 nav-font12 impact-toggle"
                [attr.aria-expanded]="expanded"
                aria-controls="refresh-impact-list"
                (click)="expanded = !expanded">
          {{ impactSummary() }}
          <i class="fas ml-1" [class.fa-chevron-down]="!expanded" [class.fa-chevron-up]="expanded"
             aria-hidden="true"></i>
        </button>

        @if (expanded) {
          <div id="refresh-impact-list" class="impact-list mt-2">
            @for (group of groups(); track group.projectId) {
              <p class="nav-font12 font-weight-bold grey5 mb-1">{{ group.projectName }}</p>
              <ul class="list-unstyled mb-2 ml-2">
                @for (report of group.reports; track report.reportId) {
                  <li class="nav-font12 grey4">
                    {{ report.reportName }}
                    @if (report.reportId === currentReportId) {
                      <span class="grey3">(this report)</span>
                    }
                  </li>
                }
              </ul>
            }
          </div>
        }
      }

      <div class="mb-3"></div>

      <div class="choice p-3 mb-2" [class.is-chosen]="!includeDatamap">
        <input type="radio" id="refresh-data-only" name="refresh-scope"
               [checked]="!includeDatamap" (change)="includeDatamap = false">
        <label class="mb-0 ml-2" for="refresh-data-only">
          <span class="nav-font14 font-weight-bold">Responses only</span>
          <span class="d-block grey4 nav-font12">
            Pulls the latest respondents. Your reports keep the questions and answer wording they
            have now.
          </span>
        </label>
      </div>

      <div class="choice p-3" [class.is-chosen]="includeDatamap">
        <input type="radio" id="refresh-with-datamap" name="refresh-scope"
               [checked]="includeDatamap" (change)="includeDatamap = true">
        <label class="mb-0 ml-2" for="refresh-with-datamap">
          <span class="nav-font14 font-weight-bold">Responses and survey structure</span>
          <span class="d-block grey4 nav-font12">
            Also re-reads the survey from Decipher, picking up questions and answers that have
            changed since this project was set up. Existing charts read their wording live, so some
            may change, and a chart whose question no longer exists will say it has no data.
          </span>
        </label>
      </div>
    </div>

    <div class="modal-footer">
      <button type="button" class="btn btn-sm btn-link grey5" (click)="close()">Cancel</button>
      <!-- Held until the blast radius is known: the whole point of listing it is to be read before
           the decision, and the dialog opens fast enough to beat the request. -->
      <button type="button" class="btn btn-sm btn-primary"
              [disabled]="impactLoading"
              (click)="confirm()">Refresh</button>
    </div>
  `,
  styles: [`
    .refresh-note {
      display: flex;
      align-items: baseline;
      padding: 10px 12px;
      border-radius: 6px;
      background: var(--nav-grey0, #f7f7f7);
      color: var(--nav-grey6);
      line-height: 1.45;
    }
    .refresh-note i {
      color: var(--nav-secondary);
    }
    .choice {
      display: flex;
      align-items: flex-start;
      border: 1px solid var(--nav-grey1);
      border-radius: 6px;
      cursor: pointer;
    }
    .choice.is-chosen {
      border-color: var(--nav-secondary);
      background: var(--nav-grey0, #f7f7f7);
    }
    .choice label {
      cursor: pointer;
    }
    .impact-toggle {
      text-decoration: underline;
    }
    /* About five rows before it scrolls: the point is to show the shape of the blast radius, and a
       client with thirty decks on one source should not push the refresh buttons off screen. */
    .impact-list {
      max-height: 11rem;
      overflow-y: auto;
    }
  `]
})
export class RefreshDataModalComponent {
  includeDatamap = false;
  expanded = false;

  /**
   * Which reports read the source being refreshed. Null when the opener didn't offer it -- the
   * reports list refreshes several sources at once, so a single blast radius doesn't apply there
   * and it opens this dialog without one.
   */
  impact: EndClientReportDataSourceUsage[] | null = null;
  impactLoading = false;

  /**
   * The lookup failed. Said out loud rather than left blank: the dialog would otherwise look like a
   * source nothing else reads, which is the opposite of what an unknown blast radius means.
   */
  impactFailed = false;

  /** So the deck the dialog was opened from can be marked rather than counted anonymously. */
  currentReportId?: string;
  currentProjectId?: string;

  /** Set by the opener; called with the choice when Refresh is pressed. */
  onConfirm?: (includeDatamap: boolean) => void;

  constructor(private modal: BsModalRef) { }

  /** Called before the request so the dialog can say it's still working that out. */
  expectImpact(currentReportId: string, currentProjectId: string) {
    this.impactLoading = true;
    this.impactFailed = false;
    this.currentReportId = currentReportId;
    this.currentProjectId = currentProjectId;
  }

  setImpact(impact: EndClientReportDataSourceUsage[]) {
    this.impact = impact;
    this.impactLoading = false;
  }

  impactUnavailable() {
    this.impact = null;
    this.impactLoading = false;
    this.impactFailed = true;
  }

  /** "5 reports, 2 of them in other projects." -- the count is the decision, the list is the detail. */
  impactSummary(): string {
    const reports = this.impact?.length ?? 0;
    const elsewhere = this.impact?.filter(u => u.projectId !== this.currentProjectId).length ?? 0;
    const plural = reports === 1 ? 'report' : 'reports';
    return elsewhere > 0
      ? `${reports} ${plural}, ${elsewhere} of them in other projects.`
      : `${reports} ${plural}, all in this project.`;
  }

  groups(): { projectId: string; projectName: string; reports: EndClientReportDataSourceUsage[] }[] {
    const byProject = new Map<string, { projectId: string; projectName: string; reports: EndClientReportDataSourceUsage[] }>();
    for (const usage of this.impact ?? []) {
      const group = byProject.get(usage.projectId)
        ?? { projectId: usage.projectId, projectName: usage.projectName, reports: [] };
      group.reports.push(usage);
      byProject.set(usage.projectId, group);
    }

    // This project first, whatever it's called: someone reading this wants their own deck's
    // neighbours before other projects'.
    return [...byProject.values()].sort((a, b) =>
      a.projectId === this.currentProjectId ? -1
        : b.projectId === this.currentProjectId ? 1
          : a.projectName.localeCompare(b.projectName));
  }

  confirm() {
    this.onConfirm?.(this.includeDatamap);
    this.close();
  }

  close() {
    this.modal.hide();
  }
}
